import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  ArrowsRightLeftIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ChartBarIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ClockIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import type { Tenant } from './TenantTable';

interface MergeAnalysis {
  conflicts: {
    type: 'users' | 'data' | 'subscriptions' | 'configuration';
    description: string;
    severity: 'low' | 'medium' | 'high';
    resolution: string;
  }[];
  dataVolume: {
    users: number;
    storage_gb: number;
    records: number;
    api_calls: number;
  };
  estimatedTime: string;
  recommendations: string[];
  risks: string[];
}

interface MergePlan {
  sourceTenantId: string;
  targetTenantId: string;
  mergeStrategy: 'preserve_both' | 'prefer_source' | 'prefer_target' | 'custom';
  dataMapping: Record<string, 'merge' | 'replace' | 'skip'>;
  userHandling: 'merge_accounts' | 'preserve_separate' | 'manual_review';
  subscriptionHandling: 'combine_billing' | 'separate_billing' | 'upgrade_target';
  scheduleFor?: Date;
}

interface AccountMergeInterfaceProps {
  isOpen: boolean;
  sourceTenant?: Tenant | null;
  onClose: () => void;
  onInitiateMerge: (mergePlan: MergePlan) => void;
}

const AccountMergeInterface: React.FC<AccountMergeInterfaceProps> = ({
  isOpen,
  sourceTenant,
  onClose,
  onInitiateMerge
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [targetTenant, setTargetTenant] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [mergeAnalysis, setMergeAnalysis] = useState<MergeAnalysis | null>(null);
  const [mergePlan, setMergePlan] = useState<Partial<MergePlan>>({
    mergeStrategy: 'prefer_target',
    dataMapping: {},
    userHandling: 'merge_accounts',
    subscriptionHandling: 'combine_billing'
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const steps = [
    { id: 1, name: 'Select Target', icon: ArrowsRightLeftIcon },
    { id: 2, name: 'Analyze Conflicts', icon: ChartBarIcon },
    { id: 3, name: 'Configure Merge', icon: Cog6ToothIcon },
    { id: 4, name: 'Review & Execute', icon: CheckCircleIcon }
  ];

  useEffect(() => {
    if (isOpen) {
      fetchAvailableTenants();
    }
  }, [isOpen]);

  useEffect(() => {
    if (sourceTenant && targetTenant) {
      analyzeMerge();
    }
  }, [sourceTenant, targetTenant]);

  const fetchAvailableTenants = async () => {
    try {
      // Simulate API call to fetch available tenants for merging
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Mock available tenants (excluding the source tenant)
      const mockTenants: Tenant[] = [
        {
          id: 'target1',
          first_name: 'John',
          last_name: 'Doe',
          email: 'john@acme-consolidated.com',
          last_login_at: '2024-08-20',
          name: 'Acme Consolidation Corp',
          domain: 'acme-consolidated.optimancer.com',
          users_count: 35,
          subscription_status: 'active',
          created_at: '2024-01-15',
          last_activity: '2024-08-20',
          monthly_revenue: 199
        },
        {
          id: 'target2',
          first_name: 'Jane',
          last_name: 'Smith',
          email: 'jane@global.com',
          last_login_at: '2024-08-21',
          name: 'Global Enterprises Ltd',
          domain: 'global.optimancer.com',
          users_count: 50,
          subscription_status: 'active',
          created_at: '2023-11-10',
          last_activity: '2024-08-21',
          monthly_revenue: 299
        }
      ].filter(t => t.id !== sourceTenant?.id);
      
      setAvailableTenants(mockTenants);
    } catch (error) {
      toast.error('Failed to load available tenants');
    }
  };

  const analyzeMerge = async () => {
    if (!sourceTenant || !targetTenant) return;

    setIsAnalyzing(true);
    try {
      // Simulate merge analysis API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockAnalysis: MergeAnalysis = {
        conflicts: [
          {
            type: 'users',
            description: 'Email address conflicts detected for 3 users',
            severity: 'medium',
            resolution: 'Append domain suffix to duplicate emails'
          },
          {
            type: 'subscriptions',
            description: 'Both tenants have active enterprise subscriptions',
            severity: 'high',
            resolution: 'Combine billing and upgrade to enterprise+ plan'
          },
          {
            type: 'configuration',
            description: 'Different API rate limits configured',
            severity: 'low',
            resolution: 'Use target tenant configuration as default'
          }
        ],
        dataVolume: {
          users: sourceTenant.users_count ?? 0,
          storage_gb: 45.2,
          records: 15420,
          api_calls: 125000
        },
        estimatedTime: '2-4 hours',
        recommendations: [
          'Schedule merge during off-peak hours (2-6 AM UTC)',
          'Notify all users 48 hours before merge execution',
          'Create full backup of both tenants before proceeding',
          'Plan for temporary API rate limit increases during migration'
        ],
        risks: [
          'Temporary service interruption during data migration',
          'Potential data loss if merge is interrupted',
          'User access issues until email conflicts are resolved',
          'Billing consolidation may affect current payment cycles'
        ]
      };

      setMergeAnalysis(mockAnalysis);
    } catch (error) {
      toast.error('Failed to analyze merge compatibility');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const updateMergePlan = (updates: Partial<MergePlan>) => {
    setMergePlan(prev => ({ ...prev, ...updates }));
  };

  const handleExecuteMerge = async () => {
    if (!sourceTenant || !targetTenant || !mergeAnalysis) return;

    const completePlan: MergePlan = {
      sourceTenantId: sourceTenant.id,
      targetTenantId: targetTenant.id,
      mergeStrategy: mergePlan.mergeStrategy!,
      dataMapping: mergePlan.dataMapping!,
      userHandling: mergePlan.userHandling!,
      subscriptionHandling: mergePlan.subscriptionHandling!,
      scheduleFor: mergePlan.scheduleFor
    };

    setIsExecuting(true);
    try {
      await onInitiateMerge(completePlan);
      toast.success('Account merge initiated successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to initiate account merge');
    } finally {
      setIsExecuting(false);
    }
  };

  const getSeverityColor = (severity: 'low' | 'medium' | 'high') => {
    switch (severity) {
      case 'low':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'medium':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'high':
        return 'text-red-700 bg-red-50 border-red-200';
    }
  };

  const filteredTenants = availableTenants.filter(tenant =>
    (tenant.name ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (tenant.domain ?? '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Select Target Tenant</h4>
              <p className="text-sm text-gray-600 mb-4">
                Choose the tenant that will remain active after the merge. Data from the source tenant will be merged into this account.
              </p>
              
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search tenants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
              </div>

              <div className="space-y-3">
                {filteredTenants.map((tenant) => (
                  <div
                    key={tenant.id}
                    onClick={() => setTargetTenant(tenant)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                      targetTenant?.id === tenant.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h5 className="font-medium text-gray-900">{tenant.name}</h5>
                        <p className="text-sm text-gray-600">{tenant.domain}</p>
                        <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                          <span className="flex items-center">
                            <UsersIcon className="h-4 w-4 mr-1" />
                            {tenant.users_count} users
                          </span>
                          <span className="flex items-center">
                            <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                            ${tenant.monthly_revenue}/mo
                          </span>
                        </div>
                      </div>
                      {targetTenant?.id === tenant.id && (
                        <CheckCircleIcon className="h-6 w-6 text-blue-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {sourceTenant && targetTenant && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                  <ArrowsRightLeftIcon className="h-4 w-4 mr-2" />
                  Merge Preview
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                  <div className="text-center">
                    <div className="font-medium text-gray-900">{sourceTenant.name}</div>
                    <div className="text-sm text-gray-500">Source (will be merged)</div>
                  </div>
                  <div className="text-center">
                    <ArrowsRightLeftIcon className="h-6 w-6 text-blue-600 mx-auto" />
                  </div>
                  <div className="text-center">
                    <div className="font-medium text-gray-900">{targetTenant.name}</div>
                    <div className="text-sm text-gray-500">Target (will remain)</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Merge Analysis</h4>
              
              {isAnalyzing ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-sm text-gray-600">Analyzing merge compatibility...</p>
                </div>
              ) : mergeAnalysis && (
                <>
                  {/* Data Volume */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h5 className="font-medium text-blue-900 mb-3">Data Volume to Migrate</h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-900">{mergeAnalysis.dataVolume.users}</div>
                        <div className="text-blue-700">Users</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-900">{mergeAnalysis.dataVolume.storage_gb}GB</div>
                        <div className="text-blue-700">Storage</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-900">{mergeAnalysis.dataVolume.records.toLocaleString()}</div>
                        <div className="text-blue-700">Records</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-900">{mergeAnalysis.dataVolume.api_calls.toLocaleString()}</div>
                        <div className="text-blue-700">API Calls</div>
                      </div>
                    </div>
                  </div>

                  {/* Conflicts */}
                  {mergeAnalysis.conflicts.length > 0 && (
                    <div className="mb-4">
                      <h5 className="font-medium text-gray-900 mb-3">Detected Conflicts</h5>
                      <div className="space-y-3">
                        {mergeAnalysis.conflicts.map((conflict, index) => (
                          <div key={index} className={`border rounded-lg p-3 ${getSeverityColor(conflict.severity)}`}>
                            <div className="flex items-start space-x-3">
                              <ExclamationTriangleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="font-medium">{conflict.description}</p>
                                <p className="text-sm mt-1">Resolution: {conflict.resolution}</p>
                                <span className="inline-block mt-2 px-2 py-1 text-xs font-medium rounded-full bg-current bg-opacity-20 capitalize">
                                  {conflict.severity} priority
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Estimated Time */}
                  <div className="flex items-center justify-center space-x-4 p-4 bg-gray-50 rounded-lg">
                    <ClockIcon className="h-5 w-5 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      Estimated merge time: {mergeAnalysis.estimatedTime}
                    </span>
                  </div>

                  {/* Recommendations and Risks */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                        <InformationCircleIcon className="h-4 w-4 mr-2 text-blue-500" />
                        Recommendations
                      </h5>
                      <ul className="space-y-2 text-sm text-gray-700">
                        {mergeAnalysis.recommendations.map((rec, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <CheckCircleIcon className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-2 text-red-500" />
                        Risks
                      </h5>
                      <ul className="space-y-2 text-sm text-gray-700">
                        {mergeAnalysis.risks.map((risk, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                            <span>{risk}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Configure Merge Settings</h4>
              
              <div className="space-y-6">
                {/* Merge Strategy */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Merge Strategy
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'prefer_target', label: 'Prefer Target', description: 'Use target tenant settings when conflicts occur' },
                      { value: 'prefer_source', label: 'Prefer Source', description: 'Use source tenant settings when conflicts occur' },
                      { value: 'preserve_both', label: 'Preserve Both', description: 'Keep both versions with prefixes/suffixes' },
                      { value: 'custom', label: 'Custom', description: 'Manual resolution for each conflict' }
                    ].map((strategy) => (
                      <label key={strategy.value} className="flex items-start space-x-3">
                        <input
                          type="radio"
                          name="mergeStrategy"
                          value={strategy.value}
                          checked={mergePlan.mergeStrategy === strategy.value}
                          onChange={(e) => updateMergePlan({ mergeStrategy: e.target.value as any })}
                          className="mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{strategy.label}</div>
                          <div className="text-sm text-gray-600">{strategy.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* User Handling */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    User Account Handling
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'merge_accounts', label: 'Merge Accounts', description: 'Automatically merge user accounts with same email' },
                      { value: 'preserve_separate', label: 'Preserve Separate', description: 'Keep all user accounts separate with email prefixes' },
                      { value: 'manual_review', label: 'Manual Review', description: 'Review each user account conflict manually' }
                    ].map((option) => (
                      <label key={option.value} className="flex items-start space-x-3">
                        <input
                          type="radio"
                          name="userHandling"
                          value={option.value}
                          checked={mergePlan.userHandling === option.value}
                          onChange={(e) => updateMergePlan({ userHandling: e.target.value as any })}
                          className="mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{option.label}</div>
                          <div className="text-sm text-gray-600">{option.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Subscription Handling */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Subscription Handling
                  </label>
                  <div className="space-y-2">
                    {[
                      { value: 'combine_billing', label: 'Combine Billing', description: 'Merge subscriptions and billing into target account' },
                      { value: 'separate_billing', label: 'Separate Billing', description: 'Keep billing separate for each original tenant' },
                      { value: 'upgrade_target', label: 'Upgrade Target', description: 'Upgrade target subscription to accommodate merged usage' }
                    ].map((option) => (
                      <label key={option.value} className="flex items-start space-x-3">
                        <input
                          type="radio"
                          name="subscriptionHandling"
                          value={option.value}
                          checked={mergePlan.subscriptionHandling === option.value}
                          onChange={(e) => updateMergePlan({ subscriptionHandling: e.target.value as any })}
                          className="mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{option.label}</div>
                          <div className="text-sm text-gray-600">{option.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Schedule */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Schedule Merge (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    min={new Date().toISOString().slice(0, 16)}
                    onChange={(e) => updateMergePlan({ 
                      scheduleFor: e.target.value ? new Date(e.target.value) : undefined 
                    })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty to execute immediately after confirmation
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Review & Execute</h4>
              
              {sourceTenant && targetTenant && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-gray-900">Source Tenant</h5>
                      <p className="text-sm text-gray-600">{sourceTenant.name}</p>
                      <p className="text-sm text-gray-600">{sourceTenant.domain}</p>
                      <p className="text-sm text-red-600">Will be deactivated after merge</p>
                    </div>
                    
                    <div>
                      <h5 className="font-medium text-gray-900">Target Tenant</h5>
                      <p className="text-sm text-gray-600">{targetTenant.name}</p>
                      <p className="text-sm text-gray-600">{targetTenant.domain}</p>
                      <p className="text-sm text-green-600">Will remain active with merged data</p>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <h5 className="font-medium text-gray-900 mb-2">Merge Configuration</h5>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p><span className="font-medium">Strategy:</span> {mergePlan.mergeStrategy?.replace('_', ' ')}</p>
                      <p><span className="font-medium">User Handling:</span> {mergePlan.userHandling?.replace('_', ' ')}</p>
                      <p><span className="font-medium">Subscription:</span> {mergePlan.subscriptionHandling?.replace('_', ' ')}</p>
                      {mergePlan.scheduleFor && (
                        <p><span className="font-medium">Scheduled:</span> {mergePlan.scheduleFor.toLocaleString()}</p>
                      )}
                    </div>
                  </div>

                  {mergeAnalysis && (
                    <div className="border-t pt-4">
                      <h5 className="font-medium text-gray-900 mb-2">Expected Results</h5>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p>• {mergeAnalysis.dataVolume.users} users will be migrated</p>
                        <p>• {mergeAnalysis.dataVolume.storage_gb}GB of data will be transferred</p>
                        <p>• {mergeAnalysis.conflicts.length} conflict{mergeAnalysis.conflicts.length !== 1 ? 's' : ''} will be resolved</p>
                        <p>• Estimated completion time: {mergeAnalysis.estimatedTime}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-700">
                    <p className="font-medium mb-1">Warning: This action cannot be undone</p>
                    <p>Once the merge is completed, the source tenant will be permanently deactivated and its data will be integrated into the target tenant. Make sure you have appropriate backups before proceeding.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <ArrowsRightLeftIcon className="h-6 w-6 text-blue-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Account Merge</h3>
                  {sourceTenant && (
                    <p className="text-sm text-gray-500">Merging: {sourceTenant.name}</p>
                  )}
                </div>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="bg-gray-50 px-6 py-4">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                    currentStep > step.id 
                      ? 'bg-green-500 border-green-500 text-white' 
                      : currentStep === step.id
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'bg-white border-gray-300 text-gray-500'
                  }`}>
                    {currentStep > step.id ? (
                      <CheckCircleIcon className="h-4 w-4" />
                    ) : (
                      <step.icon className="h-4 w-4" />
                    )}
                  </div>
                  <div className="ml-2 text-sm font-medium text-gray-700 hidden sm:block">
                    {step.name}
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 mx-4 h-0.5 bg-gray-300 hidden sm:block"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-96 overflow-y-auto">
            {renderStepContent()}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-between">
            <div>
              {currentStep > 1 && (
                <button
                  onClick={() => setCurrentStep(prev => Math.max(prev - 1, 1))}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Previous
                </button>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              
              {currentStep < 4 ? (
                <button
                  onClick={() => setCurrentStep(prev => Math.min(prev + 1, 4))}
                  disabled={
                    (currentStep === 1 && !targetTenant) ||
                    (currentStep === 2 && isAnalyzing)
                  }
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {currentStep === 2 && isAnalyzing ? 'Analyzing...' : 'Next'}
                </button>
              ) : (
                <button
                  onClick={handleExecuteMerge}
                  disabled={isExecuting}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                >
                  {isExecuting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Initiating Merge...
                    </>
                  ) : (
                    <>
                      <ArrowsRightLeftIcon className="h-4 w-4 mr-2" />
                      Execute Merge
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountMergeInterface;